import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { getContent } from './strapi/endpoints';
import { MAIN_PAGES } from './cacheService';

// Variable booléenne pour activer/désactiver le stockage dans localStorage
export const USE_LOCAL_STORAGE = false;

// Créer l'instance queryClient
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            cacheTime: 24 * 60 * 60 * 1000, // 24 heures
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retry: (failureCount, error) => {
                // Limiter les retentatives en cas d'erreur de réseau ou de 5xx
                if (error?.response?.status >= 500 && failureCount < 3) {
                    return true;
                }
                // Ne pas retenter pour les autres types d'erreurs
                return false;
            },
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        },
    },
});

// Système de keep-alive pour maintenir les données en cache
export const setupKeepAliveSystem = () => {
    // Fonction pour rafraîchir toutes les pages principales
    const refreshMainPages = () => {
        console.debug("Exécution du keep-alive pour maintenir les pages en cache");
        MAIN_PAGES.forEach(page => {
            const queryKey = ['strapi-content', page.key];
            
            // Obtenir les données actuelles
            const currentData = queryClient.getQueryData(queryKey);
            
            // Si les données existent, les "toucher" pour réinitialiser le timer de garbage collection
            if (currentData) {
                queryClient.setQueryData(queryKey, currentData);
            } else {
                // Si les données n'existent pas, les précharger
                queryClient.prefetchQuery({
                    queryKey: queryKey,
                    queryFn: async () => {
                        try {
                            const response = await getContent({
                                page: page.key,
                                components: page.components
                            });
                            return response.data.data;
                        } catch (error) {
                            console.error(`Erreur lors du préchargement de ${page.key}:`, error);
                            return null;
                        }
                    },
                    staleTime: 5 * 60 * 1000,
                    cacheTime: 24 * 60 * 60 * 1000,
                });
            }
        });
    };

    // Exécuter immédiatement une fois
    refreshMainPages();
    
    // Puis régulièrement (toutes les 10 minutes)
    const keepAliveInterval = setInterval(refreshMainPages, 10 * 60 * 1000);
    
    // Aussi lorsque l'onglet redevient visible (l'utilisateur revient sur la page)
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            refreshMainPages();
        }
    });
    
    // Nettoyage (peut être utilisé lors d'un démontage si nécessaire)
    return () => {
        clearInterval(keepAliveInterval);
        document.removeEventListener('visibilitychange', refreshMainPages);
    };
};

// Observer les suppressions de cache et recharger si nécessaire
export const setupCacheSubscriber = () => {
    const removedItemsMap = new Map(); // Pour suivre les éléments supprimés et éviter les boucles

    return queryClient.getQueryCache().subscribe(event => {
        // Si une requête est supprimée du cache
        if (event.type === 'removed') {
            const queryKey = event.query.queryKey;
            
            // Vérifier si c'est une requête Strapi
            if (queryKey?.[0] && typeof queryKey[0] === 'string' && queryKey[0].includes('strapi')) {
                const keyString = JSON.stringify(queryKey);
                console.debug(`Détection d'une requête supprimée: ${keyString}`);
                
                // Vérifier si cet élément a déjà été traité récemment
                if (removedItemsMap.has(keyString)) {
                    const lastHandled = removedItemsMap.get(keyString);
                    const now = Date.now();
                    
                    // Éviter de traiter le même élément trop fréquemment (limiter à une fois toutes les 30 secondes)
                    if (now - lastHandled < 30000) {
                        console.debug(`Élément ${keyString} déjà traité récemment, ignoré.`);
                        return;
                    }
                }
                
                // Marquer cet élément comme traité maintenant
                removedItemsMap.set(keyString, Date.now());
                
                // Nettoyer la map périodiquement pour éviter les fuites de mémoire
                if (removedItemsMap.size > 100) {
                    const now = Date.now();
                    [...removedItemsMap.entries()].forEach(([key, time]) => {
                        if (now - time > 60000) { // Supprimer les entrées de plus de 1 minute
                            removedItemsMap.delete(key);
                        }
                    });
                }
                
                // Vérifier si c'est une page principale
                const isMainPage = MAIN_PAGES.some(page => 
                    JSON.stringify(['strapi-content', page.key]) === keyString
                );
                
                // Si c'est une page principale, la recharger immédiatement
                if (isMainPage) {
                    const pageKey = queryKey[1];
                    console.debug(`Rechargement automatique de la page supprimée: ${pageKey}`);
                    const page = MAIN_PAGES.find(p => p.key === pageKey);
                    
                    if (page) {
                        // Utiliser setTimeout pour éviter de bloquer le thread principal
                        setTimeout(() => {
                            queryClient.prefetchQuery({
                                queryKey: queryKey,
                                queryFn: async () => {
                                    try {
                                        const response = await getContent({
                                            page: pageKey,
                                            components: page.components
                                        });
                                        return response.data.data;
                                    } catch (error) {
                                        console.error(`Erreur lors du rechargement de ${pageKey}:`, error);
                                        return null;
                                    }
                                },
                                staleTime: 5 * 60 * 1000, 
                                cacheTime: 24 * 60 * 60 * 1000,
                            });
                        }, 100);
                    }
                }
            }
        }
    });
};

// Configuration de la persistance (optionnelle)
export const setupPersistence = () => {
    if (USE_LOCAL_STORAGE) {
        // Création du persister qui utilise localStorage
        const localStoragePersister = createSyncStoragePersister({
            storage: window.localStorage,
            key: 'grandir-et-s-elever-cache',
            throttleTime: 1000,
        });

        // Configuration de la persistance
        persistQueryClient({
            queryClient,
            persister: localStoragePersister,
            maxAge: 24 * 60 * 60 * 1000,
            buster: '1',
        });
    }
};

// Fonction d'initialisation qui configure tout
export const initializeQueryClient = () => {
    setupPersistence();
    setupCacheSubscriber();
    setupKeepAliveSystem();
    
    return queryClient;
};